import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import 'dayjs/locale/fr';
import { backendOptions, installSafeLanguageSwitch } from './i18nReady';

// Israeli visitors overwhelmingly browse with a Hebrew-language browser, so on a
// visitor's first-ever visit (nothing stored yet) default to Hebrew when that
// signal is present, instead of always defaulting to English. Once anyone makes
// an explicit choice (toggle, or their account's saved user.language on login),
// that gets written to localStorage and always wins over this guess from then on.
const detectBrowserLanguage = () => {
  const browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || '';
  return browserLang.toLowerCase().startsWith('he') ? 'he' : 'en';
};

const storedLanguage = localStorage.getItem('language');
const currentLanguage = storedLanguage && storedLanguage !== 'null' ? storedLanguage : detectBrowserLanguage();

// Keep dayjs's active locale (used for date formatting, e.g. month names in
// the visit report / reminder date fields) in sync with the UI language, so
// dates display in Hebrew/French rather than always defaulting to English
// month names. dayjs ships "en" built-in; "he"/"fr" are imported above.
const DAYJS_LOCALES = { en: 'en', he: 'he', fr: 'fr' };
const applyDayjsLocale = (lng) => {
  dayjs.locale(DAYJS_LOCALES[lng] || 'en');
};
applyDayjsLocale(currentLanguage);
i18n.on('languageChanged', applyDayjsLocale);

// A language switch only happens once the new language's texts are loaded (see i18nReady.ts).
installSafeLanguageSwitch(i18n);

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    // REACT_APP_LOCALES_VERSION is a hash of public/locales, set by `npm run build`
    // (scripts/locales-version.js): it goes on the URL as ?v=…, so the texts of each
    // deploy have their own cacheable address.
    backend: backendOptions(process.env.REACT_APP_LOCALES_VERSION),
    fallbackLng: 'en',
    debug: false,
    ns: ['errors', 'translation'],
    defaultNS: ['translation', 'errors'],
    lng: currentLanguage,
    load: 'currentOnly',
    // React already escapes rendered values, so i18next must NOT HTML-escape
    // interpolated values — otherwise an apostrophe in a name (e.g. the project
    // "כ'ק") is turned into the literal entity "&#39;" and shown as text.
    interpolation: { escapeValue: false },
    react: {
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
      useSuspense: false,
    }
  });

export default i18n;
